import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  message,
  Tabs,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { supabase } from "../supabase";
import { useAuth } from "../contexts/AuthContext";

const { TabPane } = Tabs;

const FamilyMembers = () => {
  const { user } = useAuth();
  const [parents, setParents] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalType, setModalType] = useState("parent"); // 'parent' or 'student'
  const [form] = Form.useForm();
  const [familyId, setFamilyId] = useState(null);

  useEffect(() => {
    fetchFamilyMembers();
  }, []);

  const parentColumns = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    // Add more columns as needed
  ];

  const studentColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Age",
      dataIndex: "age",
      key: "age",
    },
    {
      title: "Access Code",
      dataIndex: "access_code",
      key: "access_code",
    },
    {
      title: "Actions",
      key: "actions",
      render: (text, record) => (
        <>
          <Button onClick={() => showEditModal(record)}>Edit</Button>
          <Button
            danger
            onClick={() => confirmDeleteStudent(record.id)}
            style={{ marginLeft: 8 }}
          >
            Delete
          </Button>
        </>
      ),
    },
    // Add more columns as needed
  ];

  const fetchFamilyMembers = async () => {
    try {
      // First get the family_id for the current user
      const { data: familyParents, error: familyError } = await supabase
        .from("family_parents")
        .select("family_id")
        .eq("parent_id", user.id)
        .single();

      if (familyError) throw familyError;

      const family_id = familyParents.family_id;
      setFamilyId(family_id);

      // Fetch parents
      const { data: parentsData, error: parentsError } = await supabase
        .from("family_parents")
        .select(
          `
          parent_id,
          users (
            id,
            email
          )
        `
        )
        .eq("family_id", family_id);

      if (parentsError) throw parentsError;

      // Fetch students
      const { data: studentsData, error: studentsError } = await supabase
        .from("students")
        .select("*")
        .eq("family_id", family_id);

      if (studentsError) throw studentsError;

      setParents(
        parentsData.map((p) => ({
          key: p.users.id,
          id: p.users.id,
          email: p.users.email,
        }))
      );
      setStudents(studentsData.map((s) => ({ ...s, key: s.id })));
    } catch (error) {
      message.error("Error fetching family members");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const showEditModal = (record) => {
    form.setFieldsValue(record);
    setModalType("student");
    setIsModalVisible(true);
  };

  const showModal = (type, record = null) => {
    setModalType(type);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    try {
      if (modalType === "parent") {
        // Get family_id
        const { data: familyData } = await supabase
          .from("family_parents")
          .select("family_id")
          .eq("parent_id", user.id)
          .single();

        // First create the user
        const { data: userData, error: userError } =
          await supabase.auth.admin.createUser({
            email: values.email,
            email_confirm: true,
          });

        if (userError) throw userError;

        // Then add them as a parent to the family
        const { error: parentError } = await supabase
          .from("family_parents")
          .insert([
            {
              family_id: familyData.family_id,
              parent_id: userData.user.id,
            },
          ]);

        if (parentError) throw parentError;
      } else {
        // Adding a student
        const { data: familyData } = await supabase
          .from("family_parents")
          .select("family_id")
          .eq("parent_id", user.id)
          .single();

        let studentError;
        if (values.id) {
          // Update existing student
          const { error } = await supabase
            .from("students")
            .update({
              name: values.name,
              age: values.age,
              access_code: values.access_code,
            })
            .eq("id", values.id);
          studentError = error;
        } else {
          // Add new student
          const { error } = await supabase.from("students").insert([
            {
              family_id: familyData.family_id,
              name: values.name,
              age: values.age,
              access_code:
                values.access_code ||
                Math.floor(1000 + Math.random() * 9000).toString(), // Generate a random 4-digit code
            },
          ]);
          studentError = error;
        }

        if (studentError) throw studentError;
      }

      message.success(`Successfully added new ${modalType}`);
      handleCancel();
      fetchFamilyMembers();
    } catch (error) {
      message.error(`Error adding ${modalType}`);
      console.error(error);
    }
  };

  const confirmDeleteStudent = (studentId) => {
    Modal.confirm({
      title: "Are you sure you want to delete this student?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: () => deleteStudent(studentId),
    });
  };

  const deleteStudent = async (studentId) => {
    try {
      const { error } = await supabase
        .from("students")
        .delete()
        .eq("id", studentId);

      if (error) throw error;

      message.success("Student deleted successfully");
      fetchFamilyMembers();
    } catch (error) {
      message.error("Error deleting student");
      console.error(error);
    }
  };

  const generateLoginCode = () => {
    const newCode = Math.floor(1000 + Math.random() * 9000).toString();
    form.setFieldsValue({ access_code: newCode });
  };

  return (
    <div style={{ padding: "24px" }}>
      <Button
        type="primary"
        onClick={() => {
          window.location.href = "/student/" + familyId;
        }}
      >
        Student Login
      </Button>
      <Tabs defaultActiveKey="1">
        <TabPane tab="Parents" key="1">
          <Button
            type="primary"
            onClick={() => showModal("parent")}
            style={{ marginBottom: 16 }}
            icon={<PlusOutlined />}
          >
            Add Parent
          </Button>
          <Table
            columns={parentColumns}
            dataSource={parents}
            loading={loading}
          />
        </TabPane>
        <TabPane tab="Students" key="2">
          <Button
            type="primary"
            onClick={() => showModal("student")}
            style={{ marginBottom: 16 }}
            icon={<PlusOutlined />}
          >
            Add Student
          </Button>
          <Table
            columns={studentColumns}
            dataSource={students}
            loading={loading}
          />
        </TabPane>
      </Tabs>

      <Modal
        title={`Add New ${
          modalType.charAt(0).toUpperCase() + modalType.slice(1)
        }`}
        open={isModalVisible}
        onCancel={handleCancel}
        onOk={form.submit}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {modalType === "parent" ? (
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: "Please input the email!" },
                { type: "email", message: "Please enter a valid email!" },
              ]}
            >
              <Input />
            </Form.Item>
          ) : (
            <>
              <Form.Item name="id" hidden />
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: "Please input the name!" }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="age"
                label="Age"
                rules={[{ required: true, message: "Please input the age!" }]}
              >
                <InputNumber min={1} />
              </Form.Item>
              <Form.Item
                label="Login Code"
                name="access_code"
                rules={[
                  { required: true, message: "Please generate a login code!" },
                ]}
              >
                <Input
                  readOnly
                  addonAfter={
                    <div
                      style={{
                        cursor: "pointer",
                        color: "#1890ff",
                        fontWeight: "bold",
                      }}
                      onClick={generateLoginCode}
                    >
                      Generate
                    </div>
                  }
                />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default FamilyMembers;
